import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db } from './firebase';

import LoginScreen from './screens/LoginScreen';
import AdminDashboard from './features/admin/AdminDashboard';
import EmployeeDashboard from './features/employee/EmployeeDashboard';
import LoadingSpinner from './components/LoadingSpinner';
import Icon from './components/Icon';

function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);

    // Data states
    const [employees, setEmployees] = useState([]);
    const [questions, setQuestions] = useState(null);

    // Authentication Listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
            if (authUser) {
                const adminDoc = await getDoc(doc(db, 'admins', authUser.uid));
                if (adminDoc.exists()) {
                    setUser({ uid: authUser.uid, role: 'admin', ...adminDoc.data() });
                } else {
                    const employeeDoc = await getDoc(doc(db, 'employees', authUser.uid));
                    if (employeeDoc.exists()) {
                        setUser({ uid: authUser.uid, role: 'employee', ...employeeDoc.data() });
                    } else {
                        console.error("User not found in admins or employees collections.");
                        setUser(null);
                    }
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Data Fetching Listeners based on user role
    useEffect(() => {
        // Ensure we don't run listeners until user is loaded
        if (!user) {
            setQuestions(null);
            setEmployees([]);
            return;
        }

        let questionsUnsubscribe;
        let employeesUnsubscribe;

        if (user.role === 'admin') {
            // Admin: Fetch all questions and all employees
            const questionsQuery = query(collection(db, 'questions'));
            questionsUnsubscribe = onSnapshot(questionsQuery, snapshot => {
                const allQuestions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setQuestions(allQuestions);
            });

            const employeesQuery = query(collection(db, 'employees'));
            employeesUnsubscribe = onSnapshot(employeesQuery, snapshot => {
                const allEmployees = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setEmployees(allEmployees);
            });
        }

        if (user.role === 'employee') {
            // Employee: Fetch ONLY their assigned questions
            // THIS IS THE CRITICAL FIX
            const questionsQuery = query(collection(db, 'questions'), where('assignedTo', '==', user.uid));
            questionsUnsubscribe = onSnapshot(questionsQuery, (snapshot) => {
                const userQuestions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setQuestions(userQuestions);
            }, (error) => {
                console.error("Error fetching employee questions:", error);
                setNotification({type: 'error', message: 'Could not fetch questions. Check Firestore rules.'});
            });
        }

        // Cleanup function to detach listeners on logout or role change
        return () => {
            if (questionsUnsubscribe) questionsUnsubscribe();
            if (employeesUnsubscribe) employeesUnsubscribe();
        };
    }, [user]); // This effect re-runs whenever the user logs in or out

    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    const handleLogout = () => {
        signOut(auth);
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    const renderContent = () => {
        if (user) {
            if (user.role === 'admin') {
                return <AdminDashboard user={user} employees={employees} questions={questions} setNotification={setNotification} />;
            }
            if (user.role === 'employee') {
                return <EmployeeDashboard user={user} questions={questions} setNotification={setNotification} />;
            }
        }
        return <LoginScreen setNotification={setNotification} />;
    };

    return (
        <div className="bg-slate-50 min-h-screen font-sans">
            <header className="bg-white/80 backdrop-blur-lg sticky top-0 z-30 border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
                    <h1 className="text-xl font-bold text-slate-900">Intern Portal</h1>
                    {user && (
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-slate-600">Welcome, <span className="font-semibold">{user.name}</span></span>
                            <button onClick={handleLogout} className="font-semibold text-sm text-indigo-600 hover:text-indigo-800">Logout</button>
                        </div>
                    )}
                </div>
            </header>
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {renderContent()}
            </main>
            {notification && (
                <div className="fixed bottom-5 right-5 bg-white p-4 rounded-lg shadow-xl border border-slate-200 flex items-center gap-3">
                    <Icon name={notification.type === 'success' ? 'CheckCircle' : 'AlertTriangle'} className={notification.type === 'success' ? 'text-emerald-500' : 'text-red-500'} />
                    <p className="text-sm text-slate-700">{notification.message}</p>
                </div>
            )}
        </div>
    );
}

export default App;

